/**
 * DecisionsPage — /agents/decisions
 * Demo page: /email (inbox: the requests waiting on you, newest first, opened in place) on the ListPage template.
 * The list is the same source as the header's "Waiting on you": pending requests this person may review. Reading and
 * deciding happen in the shared DecisionReviewDialog (components/approval); this page builds no approval UI of its own.
 * Deciding uses the routes' policies: daemon decisions need CanReviewDaemonDecision, workflow gates need
 * CanApproveWorkflowGates, so the Review control sits inside <Can> for either.
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Gavel, Inbox, RefreshCw } from 'lucide-react';
import {
  Button,
  Callout,
  DataTable,
  EmptyState,
  SearchBar,
  Toolbar,
  type DataTableColumn,
} from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { DecisionReviewDialog } from '../../components/approval/DecisionReviewDialog.js';
import { describeQueryError } from '../../components/ui/QueryBoundary.js';
import { useWaitingOnMe } from '../../hooks/useWaitingOnMe.js';
import { PAGE_HELP } from '../../help/index.js';
import type { WaitingOnMeItem } from '../../generated/api/types.gen.js';

type DecisionItem = WaitingOnMeItem & { decisionId: number };

const isDecision = (item: WaitingOnMeItem): item is DecisionItem =>
  item.kind === 'decision' && item.decisionId !== undefined;

/** The server's subtitle names the action type in snake case ("Approval · hire_employee"). */
const readableSubtitle = (subtitle: string): string => subtitle.replaceAll('_', ' ');

export const DecisionsPage: React.FC = () => {
  const help = PAGE_HELP.decisions;
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const query = useWaitingOnMe('all');

  const reviewParam = Number(params.get('decision'));
  const reviewing = Number.isInteger(reviewParam) && reviewParam > 0 ? reviewParam : null;
  const open = (decisionId: number | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (decisionId === null) next.delete('decision');
        else next.set('decision', String(decisionId));
        return next;
      },
      { replace: true },
    );

  const decisions = (query.data?.items ?? []).filter(isDecision);
  const term = search.trim().toLowerCase();
  const shown = decisions.filter(
    (d) => term === '' || d.title.toLowerCase().includes(term) || d.subtitle.toLowerCase().includes(term),
  );

  const columns: DataTableColumn<DecisionItem>[] = [
    {
      key: 'request',
      header: 'Request',
      width: '60%',
      render: (d) => (
        <div className="flex items-center gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-warning-muted text-warning">
            <Gavel size={16} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="truncate font-semibold text-heading">{d.title}</div>
            <div className="truncate text-xs text-muted">{readableSubtitle(d.subtitle)}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'waiting',
      header: 'Waiting',
      sortable: true,
      sortValue: (d) => new Date(d.updatedAt).getTime(),
      render: (d) => (
        <span className="whitespace-nowrap text-sm text-muted">
          {formatDistanceToNow(new Date(d.updatedAt), { addSuffix: true })}
        </span>
      ),
    },
    {
      key: 'review',
      header: '',
      align: 'right',
      width: '110px',
      render: (d) => (
        <Can policies={['CanReviewDaemonDecision', 'CanApproveWorkflowGates']} mode="any">
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              open(d.decisionId);
            }}
          >
            Review
          </Button>
        </Can>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Decisions"
        subtitle="Requests waiting on you. Open one to read it and approve or reject."
        helpContent={help}
        actions={
          <Button variant="secondary" size="sm" loading={query.isFetching} onClick={() => query.refetch()}>
            <RefreshCw size={14} aria-hidden="true" />
            Refresh
          </Button>
        }
        stats={[
          {
            label: 'Waiting on you',
            value: decisions.length,
            icon: Inbox,
            severity: decisions.length > 0 ? 'warning' : undefined,
          },
        ]}
        alert={
          query.isError ? (
            <Callout
              tone="danger"
              title={describeQueryError(query.error, 'your decisions')}
              actions={
                <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : undefined
        }
        toolbar={<Toolbar search={<SearchBar value={search} onChange={setSearch} placeholder="Search requests…" />} />}
      >
        <DataTable
          aria-label="Requests waiting on you"
          columns={columns}
          rows={shown}
          rowKey={(d) => d.id}
          loading={query.isPending}
          getRowAttention={() => 'warning'}
          onRowClick={(d) => open(d.decisionId)}
          empty={
            <EmptyState
              icon={Gavel}
              title={term ? 'No requests match' : help.emptyState.title}
              description={term ? 'Change the search to see more requests.' : help.emptyState.description}
              learnItems={term ? undefined : help.emptyState.learnItems}
            />
          }
        />
      </ListPage>
      <DecisionReviewDialog decisionId={reviewing} onClose={() => open(null)} />
    </>
  );
};
